import urls from "@/constants/Urls";
import { showSnackbar } from "@/store/reducers/app";
import { setAuth } from "@/store/reducers/auth";
import {
  setAccountStore,
  setDeviceStore,
  setPartnerStore,
  setPbxStore,
  setPhoneNumberStore,
} from "@/store/reducers/dataBackstage";
import {
  setAllUserStore,
  setApiTokenStore,
  setBlocklistStore,
  setCallParkingStore,
  setConferenceStore,
  setCustomDestinationStore,
  setDayNightStore,
  setDistinctiveRingtoneStore,
  setGroupStore,
  setHolidaySetStore,
  setImageStore,
  setInboundRouteStore,
  setIntercomStore,
  setIvrStore,
  setLineKeyTemplateStore,
  setMobileStore,
  setMusicOnHoldStore,
  setOutboundRuleStore,
  setPbx,
  setPbxAccounts,
  setPbxPhoneNumberStore,
  setPbxWithReset,
  setPhonebookStore,
  setProvisioningPhoneStore,
  setQueueMemberStore,
  setQueueStore,
  setRingtoneStore,
  setSoundStore,
  setTimetableStore,
  setUserStore,
  setVoicemailStore,
  setWebhookStore,
} from "@/store/reducers/dataPbx";
import { setCodecStore, setDispatcherStore, setLineKeyTypeStore } from "@/store/reducers/dataSystem";
import { setDialUserStore } from "@/store/reducers/dial";
import { clearAuthData } from "@/store/reducers/root";
import { addPbxToRecents } from "@/store/reducers/settings";
import ResourceType from "@/utils/enums/ResourceType";

import axios from "axios";

import store from "../store";

// General API setup
const CancelToken = axios.CancelToken;
let cancelRequest;

export function contextApi() {
  return axios.create({
    baseURL: `${urls.api}/${store.getState().domain.pbx.context}/`,
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${store.getState().auth.jwtToken}`,
    },
  });
}

export function globalApi() {
  return axios.create({
    baseURL: `${urls.api}/`,
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${store.getState().auth.jwtToken}`,
    },
  });
}

function createCancelToken(allowCancel = true) {
  return allowCancel
    ? new CancelToken(function executor(c) {
        cancelRequest = c;
      })
    : undefined;
}

export function createError(error) {
  if (error?.message === "Operation cancelled by user.") {
    throw "Operation cancelled by user";
  }

  if (error.response?.status === 401) {
    store.dispatch(clearAuthData());
    store.dispatch(showSnackbar("Your session has expired."));

    throw { status: error.response?.status };
  }

  if (error.response?.status === 504) {
    store.dispatch(showSnackbar("The device is not responding."));
    return;
  }

  if (error.response?.data?.errors?.message) {
    store.dispatch(showSnackbar(error.response?.data?.errors?.message));

    throw {
      status: error.response?.status,
      statusText: error.response?.data?.errors?.message,
    };
  } else {
    let errorMessage = "An unknown error occurred";

    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.errors) {
      if (Array.isArray(error.response.data.errors)) {
        errorMessage = error.response.data.errors.map((errorObject) => {
          const errors = errorObject.message.map((message) => message);
          return `${errorObject.field === "exten" ? "number" : errorObject.field}: ${errors.join(", ")}`;
        });
      } else if (Array.isArray(Object.keys(error.response.data.errors))) {
        errorMessage = Object.keys(error.response.data.errors).map((key) => {
          return `${key === "exten" ? "number" : key}: ${error.response.data.errors[key]}`;
        });
      }
    } else if (error.response?.statusText) {
      errorMessage = error.response.statusText;
    }

    store.dispatch(showSnackbar(errorMessage));

    throw {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: { ...error.response.data },
    };
  }
}

function createFileUploadError(error, setFileTasks, file) {
  if (error.name === "CanceledError") throw "Upload canceled";

  if (error.response.data.errors?.find((item) => item.field === "name")?.message[0]) {
    setFileTasks((fileTasks) =>
      fileTasks.map((task) => {
        if (task.file.name === file.name) {
          return {
            ...task,
            error:
              error.response.data.errors?.find((item) => item.field === "name")?.message[0] ??
              "An unknown error occurred...",
          };
        }

        return task;
      }),
    );

    throw "File already exists";
  }

  createError(error);
}

function updateUploadProgress(progressEvent, fileTasks, file) {
  return fileTasks.map((task) => {
    if (task.file.name === file.name) {
      return {
        ...task,
        progress: progressEvent,
      };
    }

    return task;
  });
}

export function cancelActiveGetRequests(message = "Operation cancelled by user.") {
  if (cancelRequest) {
    return cancelRequest(message);
  }
}

export function getSippyApi() {
  return globalApi().get(`/`, { cancelToken: createCancelToken() }).catch(createError);
}

// Account API
export function getAllAccounts() {
  return globalApi()
    .get(`accounts`)
    .then((response) => {
      store.dispatch(setAccountStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getAccounts() {
  return contextApi()
    .get(`accounts`)
    .then((response) => {
      store.dispatch(setPbxAccounts(response.data));
      return response;
    })
    .catch(createError);
}

export function getAccount(account, storeSettings = true) {
  return globalApi()
    .get(`accounts/${account.id}`)
    .then((response) => {
      if (storeSettings) store.dispatch(setAuth(response.data.account));
      return response;
    })
    .catch(createError);
}

export function postAccountToPbx(account, pbx) {
  return globalApi().post(`accounts/${account.id}/pbxes/${pbx.context}`).catch(createError);
}

export function deleteAccountFromPbx(account, pbx) {
  return globalApi().delete(`accounts/${account.id}/pbxes/${pbx.context}`).catch(createError);
}

export function postAccount(account) {
  return globalApi().post(`accounts`, { account }).catch(createError);
}

export function postPbxAccount(account, pbx) {
  return globalApi().post(`${pbx.context}/accounts`, { account }).catch(createError);
}

export function putAccount(account) {
  return globalApi().put(`accounts/${account.id}`, { account }).catch(createError);
}

export function storeAccount(account, pbx = null) {
  return account.id ? putAccount(account) : pbx ? postPbxAccount(account, pbx) : postAccount(account);
}

export function storePbxAccount(account, pbx) {
  return account.id ? putAccount(account) : postPbxAccount(account, pbx);
}

export function deleteAccount(account) {
  return globalApi().delete(`accounts/${account.id}`, { account }).catch(createError);
}

export function putAccountSettings(settings) {
  return globalApi().put(`accounts/settings`, { settings }).catch(createError);
}

// 2FA routes
export function getAccountTotp() {
  return globalApi().get(`accounts/totp`).catch(createError);
}

export function postAccountTotp(totp) {
  return globalApi().post(`accounts/totp`, { totp }).catch(createError);
}

export function deleteAccountTotp() {
  return globalApi().delete(`accounts/totp`).catch(createError);
}

// Authentication API
export function postLogin(email, password) {
  return globalApi().post(`auth/login`, { email, password }).catch(createError);
}

export function postLoginTotp(totp) {
  return globalApi()
    .post(`auth/login`, { provider: "totp", ...totp })
    .catch(createError);
}

export function getFirebase() {
  return globalApi().get(`auth/${store.getState().domain.pbx.context}/firebase`).catch(createError);
}

export function getWebSocketAuth() {
  return globalApi().get(`auth/ws`);
}

// PBXes API
export function getPbxes() {
  return globalApi()
    .get(`pbxes`)
    .then((response) => {
      store.dispatch(setPbxStore(response.data));
      return response;
    })
    .catch(createError);
}

export function postPbx(pbx) {
  return globalApi().post(`pbxes`, { pbx }).catch(createError);
}

export function getPbx() {
  return contextApi()
    .get(`pbxes`)
    .then((response) => {
      store.dispatch(setPbx(response.data));
      return response;
    })
    .catch(createError);
}

export function getPbxByContext(pbxContext, storeRecent = false, reset = false) {
  return globalApi()
    .get(`${pbxContext}/pbxes`)
    .then((response) => {
      if (reset) {
        store.dispatch(setPbxWithReset(response.data));
      } else {
        store.dispatch(setPbx(response.data));
      }

      if (storeRecent) {
        store.dispatch(addPbxToRecents(response.data.pbx));
      }
      return response;
    })
    .catch(createError);
}

export function getDomain(pbx) {
  return globalApi().get(`${pbx.context}/pbxes`, { cancelToken: createCancelToken() }).catch(createError);
}

export function putPbx(pbx, fixedDomain = false) {
  if (fixedDomain) {
    return globalApi().put(`${pbx.context}/pbxes`, { pbx }).catch(createError);
  } else {
    return globalApi().put(`${store.getState().domain.pbx.context}/pbxes`, { pbx }).catch(createError);
  }
}

export function deletePbx(pbx) {
  return globalApi().delete(`${pbx.context}/pbxes`, { pbx }).catch(createError);
}

export function getPbxCredit(pbx = undefined) {
  return globalApi()
    .get(`${pbx?.context ?? store.getState().domain.pbx.context}/pbxes/balance`)
    .catch(createError);
}

export function getPbxCredentials(pbx = undefined) {
  return globalApi()
    .get(`${pbx?.context ?? store.getState().domain.pbx.context}/pbxes/credentials`)
    .catch(createError);
}

// Dispatcher API
export function getDispatchers() {
  return globalApi()
    .get(`dispatchers`)
    .then((response) => {
      store.dispatch(setDispatcherStore(response.data));
      return response;
    })
    .catch(createError);
}

// Ringtones API
export function getRingtones() {
  return globalApi()
    .get(`ringtones`)
    .then((response) => {
      store.dispatch(setRingtoneStore(response.data));
      return response;
    })
    .catch(createError);
}

// Partners API
export function getPartners() {
  return globalApi()
    .get(`partners`)
    .then((response) => {
      store.dispatch(setPartnerStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getPartner(partner) {
  return globalApi().get(`partners/${partner.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postPartner(partner) {
  return globalApi().post(`partners`, { partner }).catch(createError);
}

export function putPartner(partner) {
  return globalApi().put(`partners/${partner.id}`, { partner }).catch(createError);
}

export function storePartner(partner) {
  return partner.id ? putPartner(partner) : postPartner(partner);
}

export function deletePartner(partner) {
  return globalApi().delete(`partners/${partner.id}`, { partner }).catch(createError);
}

// Mobile Users API
export function getAllUsers() {
  return contextApi()
    .get(`sippeers/all`)
    .then((response) => {
      store.dispatch(setAllUserStore(response.data));
      return response;
    })
    .catch(createError);
}

// Fixed Users API
export function getUsers() {
  return contextApi()
    .get(`sippeers`)
    .then((response) => {
      store.dispatch(setUserStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getUser(user) {
  return contextApi().get(`sippeers/${user.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postUser(user) {
  return contextApi().post(`sippeers`, { sippeer: user }).catch(createError);
}

export function putUser(user) {
  return contextApi().put(`sippeers/${user.id}`, { sippeer: user }).catch(createError);
}

export function storeUser(user) {
  return user.id ? putUser(user) : postUser(user);
}

export function deleteUser(user, remove) {
  return contextApi()
    .delete(`sippeers/${user.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

export function postUserPhonebook(user, phonebook) {
  return contextApi().post(`sippeers/${user.id}/phonebooks/${phonebook.id}`);
}

export function deleteUserPhonebook(user, phonebook) {
  return contextApi().delete(`sippeers/${user.id}/phonebooks/${phonebook.id}`);
}

export function postUserLineKey(user, lineKey) {
  return contextApi().post(`sippeers/${user.id}/linekeys`, { lineKey }).catch(createError);
}

export function putUserLineKey(user, lineKey) {
  return contextApi().put(`sippeers/${user.id}/linekeys/${lineKey.id}`, { lineKey }).catch(createError);
}

export function storeUserLineKey(user, lineKey) {
  return lineKey.id ? putUserLineKey(user, lineKey) : postUserLineKey(user, lineKey);
}

export function deleteUserLineKey(user, lineKey) {
  return contextApi().delete(`sippeers/${user.id}/linekeys/${lineKey.id}`);
}

export function postUserDistinctiveRingtone(user, distinctiveRingtone) {
  return contextApi().post(`sippeers/${user.id}/distinctiveringtones/${distinctiveRingtone.id}`);
}

export function deleteUserDistinctiveRingtone(user, distinctiveRingtone) {
  return contextApi().delete(`sippeers/${user.id}/distinctiveringtones/${distinctiveRingtone.id}`);
}

// Mobile Users API
export function getMobileUsers() {
  return contextApi()
    .get(`sippeers/mobile`)
    .then((response) => {
      store.dispatch(setMobileStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getMobileUser(user) {
  return contextApi().get(`sippeers/mobile/${user.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function putMobileUser(user) {
  return contextApi().put(`sippeers/mobile/${user.id}`, { sippeer: user }).catch(createError);
}

// Modern Phonebooks API
export function getPhonebooks() {
  return contextApi()
    .get(`phonebooks`)
    .then((response) => {
      store.dispatch(setPhonebookStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getPhonebook(phonebook, group = undefined, page = undefined) {
  return contextApi()
    .get(`phonebooks/${phonebook.id}`, {
      params: { group: group?.id, page },
      cancelToken: createCancelToken(),
    })
    .catch(createError);
}

export function postPhonebook(phonebook) {
  return contextApi().post(`phonebooks`, { phonebook }).catch(createError);
}

export function putPhonebook(phonebook) {
  return contextApi().put(`phonebooks/${phonebook.id}`, { phonebook }).catch(createError);
}

export function storePhonebook(phonebook) {
  return phonebook.id ? putPhonebook(phonebook) : postPhonebook(phonebook);
}

export function deletePhonebook(phonebook) {
  return contextApi().delete(`phonebooks/${phonebook.id}`);
}

export function getPhonebookGroup(phonebook, group, page = undefined) {
  return contextApi()
    .get(`phonebooks/${phonebook.id}/groups/${group.id}`, {
      params: { page },
      cancelToken: createCancelToken(),
    })
    .catch(createError);
}

export function postPhonebookGroup(phonebook, group) {
  return contextApi().post(`phonebooks/${phonebook.id}/groups`, { group }).catch(createError);
}

export function putPhonebookGroup(phonebook, group) {
  return contextApi().put(`phonebooks/${phonebook.id}/groups/${group.id}`, { group }).catch(createError);
}

export function storePhonebookGroup(phonebook, group) {
  return group.id ? putPhonebookGroup(phonebook, group) : postPhonebookGroup(phonebook, group);
}

export function deletePhonebookGroup(phonebook, group) {
  return contextApi().delete(`phonebooks/${phonebook.id}/groups/${group.id}`);
}

export function postPhonebookContact(phonebook, group, unit) {
  return contextApi().post(`phonebooks/${phonebook.id}/groups/${group.id}/units`, { unit }).catch(createError);
}

export function putPhonebookContact(phonebook, group, unit) {
  return contextApi()
    .put(`phonebooks/${phonebook.id}/groups/${group.id}/units/${unit.id}`, {
      unit,
    })
    .catch(createError);
}

export function storePhonebookContact(phonebook, group, unit) {
  return unit.id ? putPhonebookContact(phonebook, group, unit) : postPhonebookContact(phonebook, group, unit);
}

export function deletePhonebookContact(phonebook, group, unit) {
  return contextApi().delete(`phonebooks/${phonebook.id}/groups/${group.id}/units/${unit.id}`);
}

export function prunePhonebook(phonebook) {
  return contextApi().delete(`phonebooks/${phonebook.id}/prune`);
}

export function getPhonebookTemplate(exportTarget = "csv") {
  return contextApi()
    .get(`phonebooks/template`, { params: { type: exportTarget } })
    .catch(createError);
}

export function importPhonebook(phonebookId, groupId, file) {
  return contextApi().post(`phonebooks/${phonebookId}/groups/${groupId}/import`, file, {
    headers: {
      "Content-Type": "multipart/formdata",
    },
  });
}

export function exportPhonebook(phonebook, exportTarget = "csv") {
  return contextApi().get(`phonebooks/${phonebook.id}/export`, { params: { type: exportTarget } });
}

// Phone numbers API
export function getPhoneNumbers(page, searchDid, assigned, partner, pbx) {
  return globalApi()
    .get(`numbers`, {
      params: {
        page,
        searchDid,
        state: assigned,
        partner: partner?.id,
        pbx: pbx?.id,
      },
    })
    .then((response) => {
      store.dispatch(setPhoneNumberStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getCurrentPhoneNumbers() {
  return contextApi()
    .get(`numbers`)
    .then((response) => {
      store.dispatch(setPbxPhoneNumberStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getAvailablePhoneNumbers() {
  return contextApi()
    .get(`numbers/available`)
    .then((response) => {
      store.dispatch(setPbxPhoneNumberStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getPhoneNumber(phoneNumber) {
  return globalApi().get(`numbers/${phoneNumber.did}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postPhoneNumber(phoneNumber) {
  return globalApi().post(`numbers`, { phoneNumber }).catch(createError);
}

export function putPhoneNumber(phoneNumber) {
  return globalApi().put(`numbers/${phoneNumber.did}`, { phoneNumber }).catch(createError);
}

export function storePhoneNumber(phoneNumber) {
  return phoneNumber.did ? putPhoneNumber(phoneNumber) : postPhoneNumber(phoneNumber);
}

export function deletePhoneNumber(phoneNumber) {
  return globalApi().delete(`numbers/${phoneNumber.did}`);
}

// Music on hold API
export function getMusicOnHolds() {
  return contextApi()
    .get(`musiconhold`)
    .then((response) => {
      store.dispatch(setMusicOnHoldStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getMusicOnHold(musiconhold) {
  return contextApi().get(`musiconhold/${musiconhold.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postMusicOnHold(musiconhold) {
  return contextApi().post(`musiconhold`, { musiconhold }).catch(createError);
}

export function putMusicOnHold(musiconhold) {
  return contextApi().put(`musiconhold/${musiconhold.id}`, { musiconhold }).catch(createError);
}

export function storeMusicOnHold(musiconhold) {
  return musiconhold.id ? putMusicOnHold(musiconhold) : postMusicOnHold(musiconhold);
}

export function deleteMusicOnHold(musiconhold, remove) {
  return contextApi()
    .delete(`musiconhold/${musiconhold.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

export function postMusicOnHoldEntry(musiconhold, mohEntry) {
  return contextApi().post(`musiconhold/${musiconhold.id}/entries`, { mohEntry }).catch(createError);
}

export function putMusicOnHoldEntry(musiconhold, mohEntry) {
  return contextApi().put(`musiconhold/${musiconhold.id}/entries/${mohEntry.id}`, { mohEntry }).catch(createError);
}

export function storeMusicOnHoldEntry(musiconhold, mohEntry) {
  return musiconhold.id ? putMusicOnHoldEntry(musiconhold, mohEntry) : postMusicOnHoldEntry(musiconhold, mohEntry);
}

export function deleteMusicOnHoldEntry(musiconhold, mohEntry) {
  return contextApi().delete(`musiconhold/${musiconhold.id}/entries/${mohEntry.id}`);
}

// Codecs API
export function getCodecs() {
  return globalApi()
    .get(`codecs`)
    .then((response) => {
      store.dispatch(setCodecStore(response.data));
      return response;
    })
    .catch(createError);
}

// Inbound API
export function getInbounds() {
  return contextApi()
    .get(`inbound`)
    .then((response) => {
      store.dispatch(setInboundRouteStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getInbound(inbound) {
  return contextApi().get(`inbound/${inbound.did}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postInbound(inbound) {
  return contextApi().post(`inbound`, { inboundRoute: inbound }).catch(createError);
}

export function putInbound(inbound) {
  return contextApi().put(`inbound/${inbound.did}`, { inboundRoute: inbound }).catch(createError);
}

export function storeInbound(inbound, newRoute) {
  return !newRoute ? putInbound(inbound) : postInbound(inbound);
}

export function deleteInbound(inbound) {
  return contextApi().delete(`inbound/${inbound.did}`);
}

// Switch API
export function getDayNights() {
  return contextApi()
    .get(`switches`)
    .then((response) => {
      store.dispatch(setDayNightStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getDayNight(dayNight) {
  return contextApi().get(`switches/${dayNight.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postDayNight(dayNight) {
  return contextApi().post(`switches`, { switch: dayNight }).catch(createError);
}

export function putDayNight(dayNight) {
  return contextApi().put(`switches/${dayNight.id}`, { switch: dayNight }).catch(createError);
}

export function storeDayNight(dayNight) {
  return dayNight.id ? putDayNight(dayNight) : postDayNight(dayNight);
}

export function deleteDayNight(dayNight, remove) {
  return contextApi()
    .delete(`switches/${dayNight.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

// Conference API
export function getConferences() {
  return contextApi()
    .get(`conferences`)
    .then((response) => {
      store.dispatch(setConferenceStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getConference(conferenceRoom) {
  return contextApi().get(`conferences/${conferenceRoom.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postConference(conferenceRoom) {
  return contextApi().post(`conferences`, { conferenceRoom }).catch(createError);
}

export function putConference(conferenceRoom) {
  return contextApi().put(`conferences/${conferenceRoom.id}`, { conferenceRoom }).catch(createError);
}

export function storeConference(conferenceRoom) {
  return conferenceRoom.id ? putConference(conferenceRoom) : postConference(conferenceRoom);
}

export function deleteConference(conferenceRoom, remove) {
  return contextApi()
    .delete(`conferences/${conferenceRoom.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

// Call Parking API
export function getCallParkings() {
  return contextApi()
    .get(`parkinglots`)
    .then((response) => {
      store.dispatch(setCallParkingStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getCallParking(callParking) {
  return contextApi().get(`parkinglots/${callParking.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postCallParking(callParking) {
  return contextApi().post(`parkinglots`, { parkingLot: callParking }).catch(createError);
}

export function putCallParking(callParking) {
  return contextApi().put(`parkinglots/${callParking.id}`, { parkingLot: callParking }).catch(createError);
}

export function storeCallParking(callParking) {
  return callParking.id ? putCallParking(callParking) : postCallParking(callParking);
}

export function deleteCallParking(callParking, remove) {
  return contextApi()
    .delete(`parkinglots/${callParking.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

// Groups API
export function getGroups() {
  return contextApi()
    .get(`groups`)
    .then((response) => {
      store.dispatch(setGroupStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getGroup(group) {
  return contextApi().get(`groups/${group.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postGroup(group) {
  return contextApi().post(`groups`, { group }).catch(createError);
}

export function putGroup(group) {
  return contextApi().put(`groups/${group.id}`, { group }).catch(createError);
}

export function storeGroup(group) {
  return group.id ? putGroup(group) : postGroup(group);
}

export function deleteGroup(group, remove) {
  return contextApi()
    .delete(`groups/${group.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

export function postGroupMember(group, member) {
  return contextApi().post(`groups/${group.id}/members`, { member }).catch(createError);
}

export function putGroupMember(group, member) {
  return contextApi().put(`groups/${group.id}/members/${member.sippeer.id}`, { member }).catch(createError);
}

export function storeGroupMember(group, member) {
  return group.id ? putGroupMember(group, member) : postGroupMember(group, member);
}

export function deleteGroupMember(group, member) {
  return contextApi().delete(`groups/${group.id}/members/${member.sippeer.id}`);
}

// Intercom API
export function getIntercoms() {
  return contextApi()
    .get(`intercoms`)
    .then((response) => {
      store.dispatch(setIntercomStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getIntercom(intercom) {
  return contextApi().get(`intercoms/${intercom.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postIntercom(intercom) {
  return contextApi().post(`intercoms`, { intercom }).catch(createError);
}

export function putIntercom(intercom) {
  return contextApi().put(`intercoms/${intercom.id}`, { intercom }).catch(createError);
}

export function storeIntercom(intercom) {
  return intercom.id ? putIntercom(intercom) : postIntercom(intercom);
}

export function deleteIntercom(intercom, remove) {
  return contextApi()
    .delete(`intercoms/${intercom.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

export function postIntercomMember(intercom, member) {
  return contextApi().post(`intercoms/${intercom.id}/members/${member.id}`).catch(createError);
}

export function deleteIntercomMember(intercom, member) {
  return contextApi().delete(`intercoms/${intercom.id}/members/${member.id}`);
}

// Timetables API
export function getTimetables() {
  return contextApi()
    .get(`timetables`)
    .then((response) => {
      store.dispatch(setTimetableStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getTimetable(timetable) {
  return contextApi().get(`timetables/${timetable.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postTimetable(timetable) {
  return contextApi().post(`timetables`, { timetable }).catch(createError);
}

export function putTimetable(timetable) {
  return contextApi().put(`timetables/${timetable.id}`, { timetable }).catch(createError);
}

export function storeTimetable(timetable) {
  return timetable.id ? putTimetable(timetable) : postTimetable(timetable);
}

export function deleteTimetable(timetable, remove) {
  return contextApi()
    .delete(`timetables/${timetable.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

export function postTimetableRule(timetable, rule) {
  return contextApi().post(`timetables/${timetable.id}/rules`, { rule }).catch(createError);
}

export function putTimetableRule(timetable, rule) {
  return contextApi().put(`timetables/${timetable.id}/rules/${rule.id}`, { rule }).catch(createError);
}

export function storeTimetableRule(timetable, rule) {
  return rule.id ? putTimetableRule(timetable, rule) : postTimetableRule(timetable, rule);
}

export function deleteTimetableRule(timetable, rule) {
  return contextApi().delete(`timetables/${timetable.id}/rules/${rule.id}`);
}

export function putTimetableCondition(timetable) {
  return contextApi()
    .put(`timetables/${timetable.id}/conditions`, {
      conditions: timetable.conditions,
    })
    .catch(createError);
}

export function postTimetableHolidaySet(timetable, holidaySet) {
  return contextApi().post(`timetables/${timetable.id}/holidays/${holidaySet.id}`);
}

export function deleteTimetableHolidaySet(timetable, holidaySet) {
  return contextApi().delete(`timetables/${timetable.id}/holidays/${holidaySet.id}`);
}

// Sounds API
export function getSounds() {
  return contextApi()
    .get(`sounds`)
    .then((response) => {
      store.dispatch(setSoundStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getSound(sound) {
  return contextApi().get(`sounds/${sound.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postSound(file, controller, setFileTasks) {
  return contextApi()
    .post(
      `sounds/upload`,
      { file },
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) =>
          setFileTasks((fileTasks) => updateUploadProgress(progressEvent, fileTasks, file)),
        signal: controller.signal,
      },
    )
    .catch((error) => createFileUploadError(error, setFileTasks, file));
}

export function putSound(sound) {
  return contextApi().put(`sounds/${sound.id}`, { sound }).catch(createError);
}

export function putSoundUpload(sound, file, controller, setFileTasks) {
  return contextApi()
    .put(
      `sounds/${sound.id}/upload`,
      { file },
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) =>
          setFileTasks((fileTasks) => updateUploadProgress(progressEvent, fileTasks, file)),
        signal: controller.signal,
      },
    )
    .catch((error) => createFileUploadError(error, setFileTasks, file));
}

export function deleteSound(sound, remove) {
  return contextApi()
    .delete(`sounds/${sound.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

// Images API
export function getImages(domain = null) {
  if (domain) {
    return globalApi()
      .get(`${domain.context}/images`)
      .then((response) => {
        store.dispatch(setImageStore(response.data));
        return response;
      })
      .catch(createError);
  } else {
    return contextApi()
      .get(`images`)
      .then((response) => {
        store.dispatch(setImageStore(response.data));
        return response;
      })
      .catch(createError);
  }
}

export function getImage(image) {
  return contextApi().get(`images/${image.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postImage(file, controller, setFileTasks) {
  return contextApi()
    .post(
      `images/upload`,
      { file },
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) =>
          setFileTasks((fileTasks) => updateUploadProgress(progressEvent, fileTasks, file)),
        signal: controller.signal,
      },
    )
    .catch((error) => createFileUploadError(error, setFileTasks, file));
}

export function putImage(image) {
  return contextApi().put(`images/${image.id}`, { image }).catch(createError);
}

export function putImageUpload(image, file, controller, setFileTasks) {
  return contextApi()
    .put(
      `images/${image.id}/upload`,
      { file },
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) =>
          setFileTasks((fileTasks) => updateUploadProgress(progressEvent, fileTasks, file)),
        signal: controller.signal,
      },
    )
    .catch((error) => createFileUploadError(error, setFileTasks, file));
}

export function deleteImage(image, remove) {
  return contextApi()
    .delete(`images/${image.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

// Voicemails API
export function getVoicemails() {
  return contextApi()
    .get(`voicemails`)
    .then((response) => {
      store.dispatch(setVoicemailStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getVoicemail(voicemail) {
  return contextApi().get(`voicemails/${voicemail.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postVoicemail(voicemail) {
  return contextApi().post(`voicemails`, { voicemail }).catch(createError);
}

export function putVoicemail(voicemail) {
  return contextApi().put(`voicemails/${voicemail.id}`, { voicemail }).catch(createError);
}

export function storeVoicemail(voicemail) {
  return voicemail.id ? putVoicemail(voicemail) : postVoicemail(voicemail);
}

export function deleteVoicemail(voicemail, remove) {
  return contextApi()
    .delete(`voicemails/${voicemail.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

// Custom destinations API
export function getDestinations() {
  return contextApi()
    .get(`customdestinations`)
    .then((response) => {
      store.dispatch(setCustomDestinationStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getDestination(customDestination) {
  return contextApi()
    .get(`customdestinations/${customDestination.id}`, {
      cancelToken: createCancelToken(),
    })
    .catch(createError);
}

export function postDestination(customDestination) {
  return contextApi().post(`customdestinations`, { customDestination }).catch(createError);
}

export function putDestination(customDestination) {
  return contextApi().put(`customdestinations/${customDestination.id}`, { customDestination }).catch(createError);
}

export function storeDestination(customDestination) {
  return customDestination.id ? putDestination(customDestination) : postDestination(customDestination);
}

export function deleteDestination(customDestination, remove) {
  return contextApi()
    .delete(`customdestinations/${customDestination.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

// Queues API
export function getQueues() {
  return contextApi()
    .get(`queues`)
    .then((response) => {
      store.dispatch(setQueueStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getQueue(queue) {
  return contextApi().get(`queues/${queue.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function getQueueSilent(queue) {
  return contextApi().get(`queues/${queue.id}`, { cancelToken: createCancelToken() });
}

export function postQueue(queue) {
  return contextApi().post(`queues`, { queue }).catch(createError);
}

export function putQueue(queue) {
  return contextApi().put(`queues/${queue.id}`, { queue }).catch(createError);
}

export function storeQueue(queue) {
  return queue.id ? putQueue(queue) : postQueue(queue);
}

export function deleteQueue(queue, remove) {
  return contextApi()
    .delete(`queues/${queue.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

export function postQueueMember(queue, member, action) {
  return contextApi()
    .post(`queues/${queue.id}/members/${member.exten}`, undefined, {
      params: { action },
    })
    .catch(createError);
}

export function putQueueMember(queue, member, action) {
  return contextApi()
    .put(`queues/${queue.id}/members/${member.exten}`, undefined, {
      params: { action },
    })
    .catch(createError);
}

export function deleteQueueMember(queue, member) {
  return contextApi().delete(`queues/${queue.id}/members/${member.exten}`);
}
export function getPossibleQueueMembers() {
  return contextApi()
    .get(`queues/members`)
    .then((response) => {
      store.dispatch(setQueueMemberStore(response.data));
      return response;
    })
    .catch(createError);
}

// Blocklist API
export function getBlocklists() {
  return contextApi()
    .get(`blocklists`)
    .then((response) => {
      store.dispatch(setBlocklistStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getBlocklist(blocklist) {
  return contextApi().get(`blocklists/${blocklist.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postBlocklist(blocklist) {
  return contextApi().post(`blocklists`, { blocklist }).catch(createError);
}

export function putBlocklist(blocklist) {
  return contextApi().put(`blocklists/${blocklist.id}`, { blocklist }).catch(createError);
}

export function storeBlocklist(blocklist) {
  return blocklist.id ? putBlocklist(blocklist) : postBlocklist(blocklist);
}

export function deleteBlocklist(blocklist, remove) {
  return contextApi()
    .delete(`blocklists/${blocklist.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

export function postBlocklistRule(blocklist, rule) {
  return contextApi().post(`blocklists/${blocklist.id}/rules`, { rule }).catch(createError);
}

export function putBlocklistRule(blocklist, rule) {
  return contextApi().put(`blocklists/${blocklist.id}/rules/${rule.id}`, { rule }).catch(createError);
}

export function storeBlocklistRule(blocklist, rule) {
  return rule.id ? putBlocklistRule(blocklist, rule) : postBlocklistRule(blocklist, rule);
}

export function deleteBlocklistRule(blocklist, rule) {
  return contextApi().delete(`blocklists/${blocklist.id}/rules/${rule.id}`);
}

// Holiday Set API
export function getHolidaySets() {
  return contextApi()
    .get(`holidays`)
    .then((response) => {
      store.dispatch(setHolidaySetStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getHolidaySet(holidaySet) {
  return contextApi().get(`holidays/${holidaySet.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postHolidaySet(holidaySet) {
  return contextApi().post(`holidays`, { holidaySet }).catch(createError);
}

export function putHolidaySet(holidaySet) {
  return contextApi().put(`holidays/${holidaySet.id}`, { holidaySet }).catch(createError);
}

export function storeHolidaySet(holidaySet) {
  return holidaySet.id ? putHolidaySet(holidaySet) : postHolidaySet(holidaySet);
}

export function deleteHolidaySet(holidaySet, remove) {
  return contextApi()
    .delete(`holidays/${holidaySet.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

export function postHolidaySetHoliday(holidaySet, holidays) {
  return contextApi().post(`holidays/${holidaySet.id}/conditions`, { holidays }).catch(createError);
}

export function putHolidaySetHoliday(holidaySet, holiday) {
  return contextApi().put(`holidays/${holidaySet.id}/conditions/${holiday.id}`, { holiday }).catch(createError);
}

export function storeHolidaySetHoliday(holidaySet, holidays) {
  return holidays[0].id ? putHolidaySetHoliday(holidaySet, holidays[0]) : postHolidaySetHoliday(holidaySet, holidays);
}

export function deleteHolidaySetHoliday(holidaySet, holiday) {
  return contextApi().delete(`holidays/${holidaySet.id}/conditions/${holiday.id}`);
}

// IVRs API
export function getIvrs() {
  return contextApi()
    .get(`ivrs`)
    .then((response) => {
      store.dispatch(setIvrStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getIvr(ivr) {
  return contextApi().get(`ivrs/${ivr.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postIvr(ivr) {
  return contextApi().post(`ivrs`, { ivr }).catch(createError);
}

export function putIvr(ivr) {
  return contextApi().put(`ivrs/${ivr.id}`, { ivr }).catch(createError);
}

export function storeIvr(ivr) {
  return ivr.id ? putIvr(ivr) : postIvr(ivr);
}

export function deleteIvr(ivr, remove) {
  return contextApi()
    .delete(`ivrs/${ivr.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

export function postIvrOption(ivr, option) {
  return contextApi().post(`ivrs/${ivr.id}/options`, { ivrOption: option }).catch(createError);
}

export function putIvrOption(ivr, option) {
  return contextApi().put(`ivrs/${ivr.id}/options/${option.id}`, { ivrOption: option }).catch(createError);
}

export function storeIvrOption(ivr, option) {
  return option.id ? putIvrOption(ivr, option) : postIvrOption(ivr, option);
}

export function deleteIvrOption(ivr, option) {
  return contextApi().delete(`ivrs/${ivr.id}/options/${option.id}`);
}

// Outbound API
export function getOutbounds() {
  return contextApi()
    .get(`outbound`)
    .then((response) => {
      store.dispatch(setOutboundRuleStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getOutbound(outbound) {
  return contextApi().get(`outbound/${outbound.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postOutbound(outbound) {
  return contextApi().post(`outbound`, { outboundRule: outbound }).catch(createError);
}

export function putOutbound(outbound) {
  return contextApi().put(`outbound/${outbound.id}`, { outboundRule: outbound }).catch(createError);
}

export function storeOutbound(outbound) {
  return outbound.id ? putOutbound(outbound) : postOutbound(outbound);
}

export function deleteOutbound(outbound, remove) {
  return contextApi()
    .delete(`outbound/${outbound.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

export function postOutboundDestination(outbound, destinationPrefix) {
  return contextApi().post(`outbound/${outbound.id}/destinationprefix`, { destinationPrefix }).catch(createError);
}

export function putOutboundDestination(outbound, destinationPrefix) {
  return contextApi()
    .put(`outbound/${outbound.id}/destinationprefix/${destinationPrefix.id}`, {
      destinationPrefix,
    })
    .catch(createError);
}

export function storeOutboundDestination(outbound, destinationPrefix) {
  return destinationPrefix.id
    ? putOutboundDestination(outbound, destinationPrefix)
    : postOutboundDestination(outbound, destinationPrefix);
}

export function deleteOutboundDestination(outbound, destinationPrefix) {
  return contextApi().delete(`outbound/${outbound.id}/destinationprefix/${destinationPrefix.id}`);
}

// Devices API
export function getDevices(haslinekeys) {
  return globalApi()
    .get(`devices/specs`, { params: { haslinekeys } })
    .then((response) => {
      store.dispatch(setDeviceStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getDevice(device) {
  return globalApi()
    .get(`devices/specs`, {
      params: { type: device.type },
      cancelToken: createCancelToken(),
    })
    .catch(createError);
}

export function getDeviceById(device) {
  return globalApi().get(`devices/specs/${device.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

// Provisioning API
export function getProvisionings(state = null) {
  return contextApi()
    .get(`provisioning/phones`, { params: { state } })
    .then((response) => {
      store.dispatch(setProvisioningPhoneStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getProvisioning(device) {
  return contextApi()
    .get(`provisioning/phones/${device.mac}`, {
      cancelToken: createCancelToken(),
    })
    .catch(createError);
}

export function putProvisioning(device) {
  return contextApi().put(`provisioning/phones/${device.mac}`, { phone: device }).catch(createError);
}

export function deleteProvisioning(device) {
  return contextApi().delete(`provisioning/phones/${device.mac}`);
}

export function putProvisioningUser(device, user) {
  return contextApi().put(`provisioning/phones/${device.mac}/sippeers/${user.id}`);
}

export function deleteProvisioningUser(device, user) {
  return contextApi().delete(`provisioning/phones/${device.mac}/sippeers/${user.id}`);
}

export function getProvisioningUsers() {
  return contextApi()
    .get(`provisioning/sippeers/available`, {
      cancelToken: createCancelToken(),
    })
    .catch(createError);
}

export function actionProvisioningSync(device) {
  return contextApi().get(`provisioning/phones/${device.mac}/sync`).catch(createError);
}

export function actionProvisioningReboot(device) {
  return contextApi().get(`provisioning/phones/${device.mac}/reboot`).catch(createError);
}

export function actionProvisioningReset(device) {
  return contextApi().get(`provisioning/phones/${device.mac}/reset`).catch(createError);
}

export function postProvisioningAccount(device, account) {
  return contextApi().post(`provisioning/phones/${device.mac}/accounts`, { account }).catch(createError);
}

export function deleteProvisioningAccount(device, account) {
  return contextApi().delete(`provisioning/phones/${device.mac}/accounts/${account.id}`);
}

export function requestNewProvisioningKey() {
  return contextApi().put(`provisioning/key`);
}

// Line key templates
export function getLineKeyTypes() {
  return globalApi()
    .get(`linekeys/types`)
    .then((response) => {
      store.dispatch(setLineKeyTypeStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getLineKeyTemplates() {
  return contextApi()
    .get(`linekeys/templates`)
    .then((response) => {
      store.dispatch(setLineKeyTemplateStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getLineKeyTemplate(lineKeyTemplate) {
  return contextApi()
    .get(`linekeys/templates/${lineKeyTemplate.id}`, { cancelToken: createCancelToken() })
    .catch(createError);
}

export function postLineKeyTemplate(lineKeyTemplate) {
  return contextApi().post(`linekeys/templates`, { lineKeyTemplate }).catch(createError);
}

export function putLineKeyTemplate(lineKeyTemplate) {
  return contextApi().put(`linekeys/templates/${lineKeyTemplate.id}`, { lineKeyTemplate }).catch(createError);
}

export function storeLineKeyTemplate(lineKeyTemplate) {
  return lineKeyTemplate.id ? putLineKeyTemplate(lineKeyTemplate) : postLineKeyTemplate(lineKeyTemplate);
}

export function copyLineKeyTemplate(lineKeyTemplate) {
  return contextApi().post(`linekeys/templates/${lineKeyTemplate.id}/copy`).catch(createError);
}

export function deleteLineKeyTemplate(lineKeyTemplate, remove) {
  return contextApi()
    .delete(`linekeys/templates/${lineKeyTemplate.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

export function postLineKeyTemplateLineKey(lineKeyTemplate, lineKey) {
  return contextApi().post(`linekeys/templates/${lineKeyTemplate.id}/keys`, { lineKey }).catch(createError);
}

export function putLineKeyTemplateLineKey(lineKeyTemplate, lineKey) {
  return contextApi()
    .put(`linekeys/templates/${lineKeyTemplate.id}/keys/${lineKey.id}`, { lineKey })
    .catch(createError);
}

export function storeLineKeyTemplateLineKey(lineKeyTemplate, lineKey) {
  return lineKey.id
    ? putLineKeyTemplateLineKey(lineKeyTemplate, lineKey)
    : postLineKeyTemplateLineKey(lineKeyTemplate, lineKey);
}

export function deleteLineKeyTemplateLineKey(lineKeyTemplate, lineKey) {
  return contextApi().delete(`linekeys/templates/${lineKeyTemplate.id}/keys/${lineKey.id}`);
}

// Line key template accounts API
export function postLineKeyTemplateAccount(lineKeyTemplateAccount) {
  return contextApi().post(`linekeys/templateaccounts`, { lineKeyTemplateAccount }).catch(createError);
}

export function putLineKeyTemplateAccount(lineKeyTemplateAccount) {
  return contextApi()
    .put(`linekeys/templateaccounts/${lineKeyTemplateAccount.id}`, { lineKeyTemplateAccount })
    .catch(createError);
}

export function deleteLineKeyTemplateAccount(lineKeyTemplateAccount) {
  return contextApi().delete(`linekeys/templateaccounts/${lineKeyTemplateAccount.id}`);
}

// Webhooks API
export function getWebhooks() {
  return contextApi()
    .get(`webhooks`)
    .then((response) => {
      store.dispatch(setWebhookStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getWebhook(webhook) {
  return contextApi().get(`webhooks/${webhook.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postWebhook(webhook) {
  return contextApi().post(`webhooks`, { webhook }).catch(createError);
}

export function putWebhook(webhook) {
  return contextApi().put(`webhooks/${webhook.id}`, { webhook }).catch(createError);
}

export function storeWebhook(webhook) {
  return webhook.id ? putWebhook(webhook) : postWebhook(webhook);
}

export function deleteWebhook(webhook, remove) {
  return contextApi()
    .delete(`webhooks/${webhook.id}`, {
      headers: remove ? { "x-remove": remove } : {},
      cancelToken: createCancelToken(!remove),
    })
    .catch(createError);
}

export function postWebhookInboundRoute(webhook, inbound) {
  return contextApi().post(`webhooks/${webhook.id}/inbound/${inbound.did}`).catch(createError);
}

export function deleteWebhookInboundRoute(webhook, inbound) {
  return contextApi().delete(`webhooks/${webhook.id}/inbound/${inbound.did}`);
}

// Status API
export function getActiveUsers() {
  return contextApi().get(`status/sippeers`, { cancelToken: createCancelToken() }).catch(createError);
}

// Extension API
export function getExtension(extension) {
  return contextApi().get(`extensions/${extension}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function getExtensionResourceType(extension) {
  return contextApi()
    .get(`extensions/${extension}`, { cancelToken: createCancelToken() })
    .then((response) => {
      const typeName = Object.keys(response.data)[0];

      switch (typeName) {
        case "sippeer":
          if (response.data.sippeer.exten.startsWith("+")) {
            return ResourceType.MOBILE_USERS;
          } else {
            return ResourceType.USERS;
          }
        case "group":
          return ResourceType.GROUPS;
        case "ivr":
          return ResourceType.IVRS;
        case "voicemail":
          return ResourceType.VOICEMAILS;
        case "queue":
          return ResourceType.QUEUES;
        case "parkingLot":
          return ResourceType.CALL_PARKINGS;
        case "conferenceRoom":
          return ResourceType.CONFERENCES;
        case "intercom":
          return ResourceType.INTERCOMS;
        case "timetable":
          return ResourceType.TIMETABLES;
        case "customDestination":
          return ResourceType.CUSTOM_DESTINATIONS;
        case "switch":
          return ResourceType.DAY_NIGHTS;
        default:
          return ResourceType.UNRESTRICTED_NUMBER;
      }
    });
}

// Dial API
export function getDialUsers() {
  return globalApi()
    .get(`/${store.getState().dial.pbx.context}/sippeers`)
    .then((response) => {
      store.dispatch(setDialUserStore(response.data));
      return response;
    })
    .catch(createError);
}

export function postDialCall(phoneNumber) {
  return globalApi()
    .post(`/${store.getState().dial.pbx.context}/sippeers/${store.getState().dial.user.id}/dial/${phoneNumber}`)
    .catch(createError);
}

// Tokens API
export function getTokens() {
  return contextApi()
    .get(`pbxes/tokens`)
    .then((response) => {
      store.dispatch(setApiTokenStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getToken(token) {
  return contextApi().get(`pbxes/tokens/${token.id}`, { cancelToken: createCancelToken() }).catch(createError);
}

export function postToken(token) {
  return contextApi().post(`pbxes/tokens`, { token }).catch(createError);
}

export function putToken(token) {
  return contextApi().put(`pbxes/tokens/${token.id}`, { token }).catch(createError);
}

export function generateToken(token) {
  return contextApi().put(`pbxes/tokens/${token.id}/generate`).catch(createError);
}

export function storeToken(token) {
  return token.id ? putToken(token) : postToken(token);
}

export function deleteToken(token) {
  return contextApi().delete(`pbxes/tokens/${token.id}`).catch(createError);
}

// V4 based API
function nextApi() {
  return axios.create({
    baseURL: `${urls.nextApi}/${store.getState().domain.pbx.id}/`,
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${store.getState().auth.jwtToken}`,
    },
  });
}

function nextApiGlobal() {
  return axios.create({
    baseURL: `${urls.nextApi}/`,
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${store.getState().auth.jwtToken}`,
    },
  });
}

// User accounts
export function putVerifyAccount(token, user) {
  return nextApiGlobal()
    .put(`users/verify-user`, user ? { user } : {}, {
      headers: {
        authorization: `token ${token}`,
      },
    })
    .catch(createError);
}

export function postPasswordForgotten(user) {
  return nextApiGlobal().post(`users/password-reset`, { user }).catch(createError);
}

export function postResetPassword(user, token) {
  return nextApiGlobal()
    .put(`users/password-reset`, { user }, { headers: { authorization: `token ${token}` } })
    .catch(createError);
}

export function putEmailAddress(user, details) {
  return nextApiGlobal().put(`users/${user.id}/email`, { user: details }).catch(createError);
}

export function postResendEmailConfirmation(user) {
  return nextApiGlobal().post(`users/${user.id}/resend-email-confirmation`).catch(createError);
}

export function putVerifyEmail(token) {
  return nextApiGlobal().put(
    `users/verify-email`,
    {},
    {
      headers: {
        authorization: `token ${token}`,
      },
    },
  );
}

// Distinctive Ringtones
export function getDistinctiveRingtones() {
  return nextApi()
    .get(`distinctiveringtones`)
    .then((response) => {
      store.dispatch(setDistinctiveRingtoneStore(response.data));
      return response;
    })
    .catch(createError);
}

export function getDistinctiveRingtone(distinctiveRingtone) {
  return nextApi()
    .get(`distinctiveringtones/${distinctiveRingtone.id}`, { cancelToken: createCancelToken() })
    .catch(createError);
}

export function postDistinctiveRingtone(distinctiveRingtone) {
  return nextApi().post(`distinctiveringtones`, { distinctiveRingtone }).catch(createError);
}

export function putDistinctiveRingtone(distinctiveRingtone) {
  return nextApi().put(`distinctiveringtones/${distinctiveRingtone.id}`, { distinctiveRingtone }).catch(createError);
}

export function storeDistinctiveRingtone(distinctiveRingtone) {
  return distinctiveRingtone.id
    ? putDistinctiveRingtone(distinctiveRingtone)
    : postDistinctiveRingtone(distinctiveRingtone);
}

export function pruneDistinctiveRingtone(distinctiveRingtone) {
  return nextApi().delete(`distinctiveringtones/${distinctiveRingtone.id}/prune`).catch(createError);
}

export function applyDistinctiveRingtone(distinctiveRingtone) {
  return nextApi().post(`distinctiveringtones/${distinctiveRingtone.id}/apply`).catch(createError);
}

export function deleteCheckDistinctiveRingtone(distinctiveRingtone) {
  return nextApi().get(`distinctiveringtones/${distinctiveRingtone.id}/occurrences`).catch(createError);
}

export function deleteDistinctiveRingtone(distinctiveRingtone) {
  return nextApi().delete(`distinctiveringtones/${distinctiveRingtone.id}`).catch(createError);
}
